import { Component, input, signal, computed, OnInit, output, OnDestroy } from '@angular/core';
import { BooleanOperators, DateOperators, EnumOperators, NumberOperators, StringOperators } from '@shared/enums';
import { ColumnActiveFilter, ColumnToFilter, DropdownOption } from '@shared/interfaces/dev';
import {
  BooleanFilterOperator,
  ColumnsFilterEvent,
  EmbeddedKeys,
  EnumFilterOperator,
  FilterOperator,
  NumberFilterOperator,
  StringFilterOperator,
} from '@shared/types/dev';
import { DropdownChangeEvent } from 'primeng/dropdown';

@Component({
  selector: 'idel-table-columns-filter',
  templateUrl: './table-columns-filter.component.html',
  styleUrl: './table-columns-filter.component.scss',
})
export class TableColumnsFilterComponent<T extends object> implements OnInit, OnDestroy {
  public readonly columns = input.required<ColumnToFilter<T>[]>();
  /**
   * @warning Use a unique key for each table to avoid conflicts in session storage
   */
  public readonly sessionStorageKey = input<string>('table-columns-filter');

  public readonly onFilter = output<ColumnsFilterEvent<T>>();
  public readonly onReset = output<void>();

  public readonly activeFilters = signal<ColumnActiveFilter<T>[]>([]);
  public readonly isFiltersDialogOpen = signal<boolean>(false);

  public readonly canAddFilter = computed<boolean>(
    () => this.columns().length > 0 && this.activeFilters().length < this.columns().length
  );
  public readonly hasActiveFilters = signal<boolean>(false);

  public readonly stringOperatorsOptions = signal<DropdownOption<StringFilterOperator>[]>([
    { label: 'Est', value: StringOperators.EQUALS },
    { label: 'Contient', value: StringOperators.CONTAINS },
    { label: 'Commence par', value: StringOperators.STARTS_WITH },
    { label: 'Finit par', value: StringOperators.ENDS_WITH },
    { label: 'Est vide', value: StringOperators.IS_EMPTY },
    { label: "N'est pas vide", value: StringOperators.IS_NOT_EMPTY },
  ]);
  public readonly numberOperatorsOptions = signal<DropdownOption<NumberFilterOperator>[]>([
    { label: 'Vaut', value: NumberOperators.EQUALS },
    { label: 'Est supérieur à', value: NumberOperators.GREATER_THAN },
    { label: 'Est inférieur à', value: NumberOperators.LESS_THAN },
    { label: 'Vaut 0 ', value: NumberOperators.ZERO },
    { label: 'Est vide', value: NumberOperators.IS_EMPTY },
    { label: "N'est pas vide", value: NumberOperators.IS_NOT_EMPTY },
  ]);
  public readonly dateOperatorsOptions = signal<DropdownOption<DateOperators>[]>([
    { label: 'Est', value: DateOperators.EQUALS },
    { label: 'Avant le', value: DateOperators.BEFORE },
    { label: 'Après le', value: DateOperators.AFTER },
    { label: 'Est vide', value: DateOperators.IS_EMPTY },
    { label: "N'est pas vide", value: DateOperators.IS_NOT_EMPTY },
  ]);
  public readonly booleanOperatorsOptions = signal<DropdownOption<BooleanFilterOperator>[]>([
    { label: 'Est vrai', value: BooleanOperators.IS_TRUE },
    { label: 'Est faux', value: BooleanOperators.IS_FALSE },
    { label: 'Est vide', value: BooleanOperators.IS_EMPTY },
    { label: "N'est pas vide", value: BooleanOperators.IS_NOT_EMPTY },
  ]);
  public readonly enumOperatorsOptions = signal<DropdownOption<EnumFilterOperator>[]>([
    { label: 'Est', value: EnumOperators.EQUALS },
    { label: "N'est pas", value: EnumOperators.NOT_EQUALS },
    { label: 'Est vide', value: EnumOperators.IS_EMPTY },
    { label: "N'est pas vide", value: EnumOperators.IS_NOT_EMPTY },
  ]);
  public windowEventListener: void;
  windowEventListenerHandler = () => sessionStorage.removeItem(this.sessionStorageKey());

  constructor() {
    window.addEventListener('beforeunload', this.windowEventListenerHandler);
  }

  ngOnInit(): void {
    const savedFilters = sessionStorage.getItem(this.sessionStorageKey());

    if (savedFilters) {
      this.activeFilters.set(JSON.parse(savedFilters));
      return;
    }
    this.setFirstColumnFilterAsActive();
  }

  ngOnDestroy(): void {
    window.removeEventListener('beforeunload', this.windowEventListenerHandler);
  }

  private saveFiltersToSessionStorage(): void {
    sessionStorage.setItem(this.sessionStorageKey(), JSON.stringify(this.activeFilters()));
  }

  private getDefaultOperatorForFieldType(
    fieldType: 'string' | 'number' | 'date' | 'boolean' | 'enum',
    multiSelection?: boolean
  ): FilterOperator {
    switch (fieldType) {
      case 'string':
        return StringOperators.CONTAINS;
      case 'number':
        return NumberOperators.EQUALS;
      case 'date':
        return DateOperators.EQUALS;
      case 'boolean':
        return BooleanOperators.IS_TRUE;
      case 'enum':
        return multiSelection ? EnumOperators.SOME : EnumOperators.EQUALS;
      default:
        return StringOperators.CONTAINS;
    }
  }

  private setFirstColumnFilterAsActive(): void {
    if (!this.columns().length) return;

    const firstColumnToFilter = this.columns()[0];
    this.activeFilters.set([
      {
        field: firstColumnToFilter.field,
        operator: this.getDefaultOperatorForFieldType(firstColumnToFilter.type, firstColumnToFilter?.multiSelection),
        value: null,
      },
    ]);
  }

  public getOperatorTypeOptions(field: keyof T | EmbeddedKeys<T>): DropdownOption<FilterOperator>[] {
    const columnToFilter = this.columns().find((column) => column.field === field);

    if (!columnToFilter) {
      throw new Error(`Column with field ${String(field)} not found`);
    }

    switch (columnToFilter.type) {
      case 'string':
        return this.stringOperatorsOptions();
      case 'number':
        return this.numberOperatorsOptions();
      case 'date':
        return this.dateOperatorsOptions();
      case 'boolean':
        return this.booleanOperatorsOptions();
      case 'enum':
        return this.enumOperatorsOptions();
      default:
        return [];
    }
  }

  public getAvailableFieldsToFilterOptions(forField: keyof T | EmbeddedKeys<T>): DropdownOption<keyof T>[] {
    return this.columns().map((column) => ({
      label: column.fieldLabel,
      value: column.field,
      disabled:
        this.activeFilters().some((filter) => filter.field === column.field && filter.field !== forField) ||
        (typeof column.hide === 'function' ? column.hide() : !!column.hide),
    })) as DropdownOption<keyof T>[];
  }

  public getActiveFilterFieldType(
    activeFilter: ColumnActiveFilter<T>
  ): 'string' | 'number' | 'date' | 'boolean' | 'enum' {
    const columnToFilter = this.columns().find((column) => column.field === activeFilter.field);

    if (!columnToFilter) {
      throw new Error(`Column with field ${String(activeFilter.field)} not found`);
    }

    return columnToFilter.type;
  }

  public getActiveFilterMultiSelection(activeFilter: ColumnActiveFilter<T>): boolean {
    const columnToFilter = this.columns().find((column) => column.field === activeFilter.field);
    if (!columnToFilter) {
      throw new Error(`Column with field ${String(activeFilter.field)} not found`);
    }

    return columnToFilter.multiSelection ?? false;
  }

  public toggleFiltersDialog(): void {
    this.isFiltersDialogOpen.update((current) => !current);
  }

  public onAddNewActiveFilter(): void {
    const nextAvailableColumnToFilter = this.columns().filter(
      (column) => !this.activeFilters().some((filter) => filter.field === column.field)
    )[0];

    if (!nextAvailableColumnToFilter) return;

    this.activeFilters.update((current) => [
      ...current,
      {
        field: nextAvailableColumnToFilter.field,
        operator: this.getDefaultOperatorForFieldType(
          nextAvailableColumnToFilter.type,
          nextAvailableColumnToFilter?.multiSelection
        ),
        value: null,
      },
    ]);
  }

  public getColumnOptions(activeFilter: ColumnActiveFilter<T>): DropdownOption<any>[] {
    const columnToFilter = this.columns().find((column) => column.field === activeFilter.field);

    if (!columnToFilter) {
      throw new Error(`Column with field ${String(activeFilter.field)} not found`);
    }

    return columnToFilter.options ?? [];
  }

  public onDeleteActiveFilter(activeFilter: ColumnActiveFilter<T>): void {
    if (this.activeFilters().length === 1) return;

    this.activeFilters.update((current) => current.filter((filter) => filter !== activeFilter));
  }

  public resetActiveFilterValue(activeFilter: ColumnActiveFilter<T>): void {
    this.setOperatorForMultiSelectionIfNeeded(activeFilter, this.columns());

    this.activeFilters.update((current) =>
      current.map((filter) =>
        filter === activeFilter
          ? {
              ...filter,
              value: null,
            }
          : filter
      )
    );
  }

  private setOperatorForMultiSelectionIfNeeded(
    activeFilter: ColumnActiveFilter<T>,
    columns: ColumnToFilter<T>[]
  ): void {
    const columnToFilter = columns.find((column) => column.field === activeFilter.field);
    if (!columnToFilter) {
      throw new Error(`Column with field ${String(activeFilter.field)} not found`);
    }

    if (columnToFilter.multiSelection && activeFilter.operator !== EnumOperators.SOME) {
      activeFilter.operator = EnumOperators.SOME;
    }

    if (!columnToFilter.multiSelection && activeFilter.operator === EnumOperators.SOME) {
      activeFilter.operator = EnumOperators.EQUALS;
    }
  }

  public canFilter(): boolean {
    return this.activeFilters().every((filter) => {
      if (
        filter.operator === BooleanOperators.IS_TRUE ||
        filter.operator === BooleanOperators.IS_FALSE ||
        filter.operator === 'isEmpty' ||
        filter.operator === 'isNotEmpty'
      ) {
        return true;
      }

      return filter.value !== null && filter.value !== undefined && filter.value !== '';
    });
  }

  public onActiveFilterOperatorChange(event: DropdownChangeEvent, activeFilter: ColumnActiveFilter<T>): void {
    const { value } = event;

    if (activeFilter.operator !== BooleanOperators.IS_TRUE && activeFilter.operator !== BooleanOperators.IS_FALSE) {
      this.activeFilters.update((current) =>
        current.map((filter) =>
          filter === activeFilter
            ? {
                ...filter,
                operator: value,
                value: null,
              }
            : filter
        )
      );
      return;
    }

    this.activeFilters.update((current) =>
      current.map((filter) =>
        filter === activeFilter
          ? {
              ...filter,
              operator: value,
              value: activeFilter.operator === BooleanOperators.IS_TRUE,
            }
          : filter
      )
    );
  }

  public onResetFilters(): void {
    this.activeFilters.set([]);
    this.setFirstColumnFilterAsActive();
    this.saveFiltersToSessionStorage();
    this.isFiltersDialogOpen.set(false);
    this.hasActiveFilters.set(false);

    this.onReset.emit();
  }

  public onFilterClick(): void {
    if (!this.canFilter()) return;

    this.onFilter.emit({
      nbrFilters: this.activeFilters().length,
      filters: this.activeFilters(),
    });
    this.hasActiveFilters.set(true);
    this.saveFiltersToSessionStorage();
    this.isFiltersDialogOpen.set(false);
  }

  public onFilterValueChange(activeFilter: ColumnActiveFilter<T>, event: DropdownChangeEvent): void {
    const { value } = event;
    const activeFilters = this.activeFilters();

    let filterToUpdate = activeFilters.find((filter) => filter.field === activeFilter.field);

    if (!filterToUpdate) return;

    filterToUpdate = { ...filterToUpdate, value };

    const filterIndex = activeFilters.findIndex((filter) => filter.field === activeFilter.field);
    activeFilters[filterIndex] = filterToUpdate;
    this.activeFilters.update((current) => [...current]);
  }
}
